import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import * as d3 from 'd3';
import _ from 'lodash';

import {type RGBA} from '../../util/colors';
import {NULL_STRING, NULL_STRING_ANGLE_BRACKETS} from '../../util/constants';
import * as Run from '../../util/runs';
import type * as RunTypes from '../../util/runTypes';
import {type PanelSelectionConstraint} from '../../util/selectionmanager';
import {type PCColumn} from '../PanelParallelCoord/common';

// Utility functions for the Parallel Coordinates plot (PanelParallelCoord.tsx)
//
// This file defines:
// - rowsToYData(), which is the main function to get plot data from run data
// - NumericDimension and StringDimension classes, used internally by rowsToYData
// - a few small helper functions.

export type Dimension = NumericDimension | StringDimension;

type AccessorWithDimension = {
  accessor: string;
  dimension: Dimension;
};

export function getAccessorsWithDimensions(
  rows: Array<{[key: string]: RunTypes.Value}>,
  columns: PCColumn[],
  plotScale: d3.ScaleLinear<any, any>,
  gradientColors: number[][],
  grayIndex: number,
  posInfColorIndex: number,
  negInfColorIndex: number
): AccessorWithDimension[] {
  if (rows.length === 0) {
    return [];
  }

  const accessorSet: Set<string> = new Set();
  const numAccessorSet: Set<string> = new Set();
  for (const row of rows) {
    for (const [accessor, value] of Object.entries(row)) {
      accessorSet.add(accessor);
      if (NumericDimension.isVal(value)) {
        numAccessorSet.add(accessor);
      }
    }
  }

  return columns
    .map(column => {
      const {accessor} = column;
      if (
        accessor == null ||
        accessor === 'name' ||
        !accessorSet.has(accessor)
      ) {
        return null;
      }
      if (numAccessorSet.has(accessor)) {
        return {
          accessor,
          dimension: new NumericDimension(
            column,
            rows,
            plotScale,
            gradientColors,
            grayIndex,
            posInfColorIndex,
            negInfColorIndex
          ),
        };
      }
      return {
        accessor,
        dimension: new StringDimension(
          column,
          rows,
          plotScale,
          gradientColors,
          grayIndex
        ),
      };
    })
    .filter(isNotNullOrUndefined);
}

/* DIMENSIONS */

class NumericDimension {
  static toVal(val: any): number | null {
    if (typeof val === 'number') {
      return val;
    }

    if ([NaN, 'NaN', 'nan'].includes(val)) {
      return NaN;
    }
    if ([Infinity, 'Infinity', 'Infinite'].includes(val)) {
      return Infinity;
    }
    if ([-Infinity, '-Infinity', '-Infinite'].includes(val)) {
      return -Infinity;
    }

    // Allows things like semantic versioning (1.2.4) to be displayed as a string
    if (Number.isNaN(Number(val))) {
      return null;
    }

    if (typeof val === 'string') {
      const parsedFloat = parseFloat(val);
      if (Number.isFinite(parsedFloat)) {
        return parsedFloat;
      }
    }

    return null;
  }

  static isVal(val: any): boolean {
    return NumericDimension.toVal(val) != null;
  }

  brush?: d3.BrushBehavior<any>;
  minVal: number;
  maxVal: number;
  hasNull: boolean;
  hasInfinity: boolean;
  hasNaN: boolean;
  values: Array<number | null>;
  numScale: d3.ScaleContinuousNumeric<number, number>;
  nanScale: d3.ScaleOrdinal<string, number>;
  finiteSpace: number;
  finiteRangeStart: number;
  finiteRangeEnd: number;
  normScale: any;
  plotScale: any;
  normVals: any;
  plotVals: any;
  nanColorScale: d3.ScaleOrdinal<string, number>;
  numColorScale?: d3.ScaleContinuousNumeric<number, number>;
  colorScale: any;
  colors: any;
  colorStartY: any;
  colorEndY: any;
  getSelectionConstraint: (
    d3Selection: number[],
    axisIsInverted?: boolean
  ) => PanelSelectionConstraint;

  constructor(
    {accessor, log, inverted}: PCColumn,
    runs: Array<{[key: string]: RunTypes.Value}>,
    plotScale: any,
    gradientColors: number[][],
    grayIndex: number,
    posInfColorIndex: number,
    negInfColorIndex: number
  ) {
    const d3ScaleFn = log ? d3.scaleLog : d3.scaleLinear;

    this.minVal = Infinity;
    this.maxVal = -Infinity;
    this.hasNull = false;
    this.hasInfinity = false;
    this.hasNaN = false;

    // Returns the groupSelections.selections.bounds for this axis
    this.getSelectionConstraint = (
      d3Selection: number[],
      axisIsInverted = false
    ): PanelSelectionConstraint => {
      const [high, low] = d3Selection.map(v => {
        return this.plotScale.invert(v);
      });
      if (low === high) {
        return {};
      }
      if (axisIsInverted) {
        return {low: high, high: low};
      }
      return {low, high};
    };

    this.values = runs.map(run => {
      if (accessor == null) {
        return null;
      }
      const val = NumericDimension.toVal(run[accessor]);
      if (!log) {
        return val;
      }

      // Filter out negative vals in log plots
      if (val != null && val < 0) {
        return null;
      }
      if (val === 0) {
        return -Infinity;
      }
      return val;
    });
    this.values.forEach(val => {
      if (val == null) {
        this.hasNull = true;
        return;
      }
      if (Number.isNaN(val)) {
        this.hasNaN = true;
        return;
      }
      if (!Number.isFinite(val)) {
        this.hasInfinity = true;
        return;
      }
      if (val < this.minVal) {
        this.minVal = val;
      }
      if (val > this.maxVal) {
        this.maxVal = val;
      }
    });
    // d3.scaleLog.domain.ticks returns an empty array if one of the bounds is 0
    if (log && this.minVal === 0) {
      this.minVal = 0.000001;
    }

    this.numScale = d3ScaleFn().domain(
      inverted ? [this.maxVal, this.minVal] : [this.minVal, this.maxVal]
    );
    if (!log) {
      this.numScale.nice();
    }

    let numScaleTicks = this.numScale.ticks();
    if (numScaleTicks.length <= 1) {
      // This fixes https://wandb.atlassian.net/browse/WB-6266 where we sometimes get
      // a single tick back and it breaks the selector. I could see an argument for
      // always adding minVal and maxVal if it's not contained within the ticks.
      numScaleTicks = [this.minVal, this.maxVal];
    }
    const firstTick = numScaleTicks[0];
    const lastTick = numScaleTicks[numScaleTicks.length - 1];

    // portions of the axis for different sections
    // finiteSpace: range of finite numbers, a superset of [this.min, this.max]
    // infiniteSpace: -Infinity to Infinity (if data has infinities)
    // totalSpace: null, NaN, (if data has them) + infiniteSpace
    // d3 decides how many ticks there will be based on the domain

    this.finiteSpace = Math.max(1, numScaleTicks.length - 1);
    const infiniteSpace = this.finiteSpace + (this.hasInfinity ? 2 : 0);
    const totalSpace =
      infiniteSpace +
      (this.hasNaN && this.hasNull ? 3 : this.hasNaN || this.hasNull ? 2 : 0);

    // null, NaN, and infinities
    const nanDomain: string[] = [];
    // tick text for non-finite values
    const preFiniteTickVals = [];
    const postFiniteTickVals = [];
    if (this.hasNull) {
      nanDomain.push('null');
    }
    if (this.hasNaN) {
      nanDomain.push('NaN');
    }
    if (this.hasNull || this.hasNaN) {
      preFiniteTickVals.push(0);
    }
    if (this.hasNull && this.hasNaN) {
      preFiniteTickVals.push(1 / totalSpace);
    }
    if (this.hasInfinity) {
      nanDomain.push((-Infinity).toString());
      nanDomain.push(Infinity.toString());
      preFiniteTickVals.push(1 - infiniteSpace / totalSpace);
      postFiniteTickVals.push(1);
      this.finiteRangeStart = 1 - (this.finiteSpace + 1) / totalSpace;
      this.finiteRangeEnd = 1 - 1 / totalSpace;
    } else {
      this.finiteRangeStart = 1 - this.finiteSpace / totalSpace;
      this.finiteRangeEnd = 1;
    }

    this.nanScale = d3
      .scaleOrdinal(_.concat(preFiniteTickVals, postFiniteTickVals))
      .domain(nanDomain);

    this.numScale.range([this.finiteRangeStart, this.finiteRangeEnd]);

    this.normScale = fakeScale(
      (v: number | string | null) => {
        if (typeof v === 'number' && Number.isFinite(v)) {
          return this.numScale(v) as number;
        }
        return this.nanScale(String(v));
      },
      (v: number) => this.numScale.invert(v),
      [0, 1]
    );

    this.plotScale = fakeScale(
      v => plotScale(this.normScale(v)),
      v => this.normScale.invert(plotScale.invert(v)),
      plotScale.range()
    );

    const tickValues = _.sortBy(
      [...nanDomain, ...numScaleTicks],
      this.normScale // sort by normScale because plotScale may have a different order
    );
    this.plotScale.ticks = () => tickValues; // breaks the ticks interface slightly by ignoring the count argument
    this.plotScale.tickFormat = (count?: number, specifier?: string) => {
      const numFormat = this.numScale.tickFormat(count, specifier);
      const isDuration =
        accessor != null ? Run.isDurationKeyString(accessor) : false;

      return (tickValue: number | string | null): string => {
        if (tickValue == null || tickValue === 'null') {
          return NULL_STRING;
        }
        if (tickValue === -Infinity) {
          return '-∞';
        }
        if (tickValue === Infinity) {
          return '∞';
        }
        if (Number.isNaN(Number(tickValue))) {
          return 'NaN';
        }
        if (typeof tickValue === 'number' && Number.isFinite(tickValue)) {
          if (isDuration) {
            return Run.formatDuration(tickValue);
          }
          return numFormat(tickValue);
        }
        return String(tickValue);
      };
    };

    this.normVals = this.values.map(this.normScale);
    this.plotVals = this.normVals.map(plotScale);

    this.nanColorScale = d3
      .scaleOrdinal([grayIndex, grayIndex, negInfColorIndex, posInfColorIndex])
      .domain([
        'null',
        NaN.toString(),
        (-Infinity).toString(),
        Infinity.toString(),
      ]);

    // legacy case
    this.numColorScale = d3ScaleFn()
      .domain([firstTick, lastTick])
      .range([0, gradientColors.length - 1]);

    this.colorScale = (v: number | null): number => {
      if (v == null || !Number.isFinite(v) || this.numColorScale == null) {
        return this.nanColorScale(v?.toString() ?? 'null');
      }
      // fix rounding bug where v could be smaller than the lower bound of the color scale
      v = _.clamp(
        v,
        Math.min(firstTick, lastTick),
        Math.max(firstTick, lastTick)
      );
      return Math.round(this.numColorScale(v) as number);
    };

    this.colors = this.values.map(this.colorScale);
    this.colorStartY = this.plotScale(
      inverted
        ? Math.max(firstTick, this.maxVal)
        : Math.min(firstTick, this.minVal)
    );
    this.colorEndY = this.plotScale(
      inverted
        ? Math.min(lastTick, this.minVal)
        : Math.max(lastTick, this.maxVal)
    );
  }
}

class StringDimension {
  static toVal(val: any): string {
    if (val == null) {
      return NULL_STRING_ANGLE_BRACKETS;
    }
    if (typeof val === 'string') {
      return val;
    }
    return val.toString();
  }

  brush?: d3.BrushBehavior<any>;
  domain: string[];
  tickValues: Array<number | string | null>;
  normScale: d3.ScaleOrdinal<string, unknown>;
  inverseNormScale: d3.ScaleLinear<number, number>;
  plotScale: any;
  normVals: any;
  plotVals: any;
  colorScale: any;
  colors: any;
  colorStartY: any;
  colorEndY: any;
  getSelectionConstraint: (d3Selection: number[]) => PanelSelectionConstraint;

  constructor(
    column: PCColumn,
    runs: Array<{[key: string]: RunTypes.Value}>,
    plotScale: any,
    gradientColors: number[][],
    grayIndex: number
  ) {
    const stringValues = runs.map(run => {
      return StringDimension.toVal(run[column.accessor ?? '']);
    });

    this.domain = _(stringValues).sortBy().sortedUniq().value();

    if (this.domain.length <= 20) {
      this.tickValues = _.clone(this.domain);
    } else {
      this.tickValues = _.range(0, 1.01, 0.1).map(f => {
        return this.domain[Math.round(f * (this.domain.length - 1))];
      });
    }

    // Returns the groupSelections.selections.bounds for this axis
    this.getSelectionConstraint = (
      d3Selection: number[]
    ): PanelSelectionConstraint => {
      const [high, low] = d3Selection.map(v => {
        return this.plotScale.invert(v);
      });

      const fromIndex = this.domain.indexOf(low);
      const toIndex = this.domain.indexOf(high) + 1;
      const domainSlice = this.domain.slice(fromIndex, toIndex);

      return {match: domainSlice};
    };

    const normScaleRange = _.range(0, 1.01, 1 / (this.domain.length - 1));
    this.normScale = d3
      .scaleOrdinal()
      .domain(this.domain)
      .range(normScaleRange)
      .unknown(undefined);

    this.inverseNormScale = d3
      .scaleLinear()
      .domain([0, 1])
      .range([0, this.domain.length - 1]);

    this.plotScale = fakeScale(
      (v: string | number | null) => plotScale(this.normScale(String(v))),
      (v: number) =>
        this.domain[
          Math.round(this.inverseNormScale(plotScale.invert(v)) as number)
        ],
      plotScale.range()
    );
    this.plotScale.ticks = () => this.tickValues;
    this.plotScale.tickFormat = () => {
      return (tick: number | string | null): string => {
        if (tick == null) {
          return NULL_STRING;
        }
        if (typeof tick === 'string') {
          return tick;
        }
        return tick.toString();
      };
    };

    this.normVals = stringValues.map(this.normScale);
    this.plotVals = this.normVals.map(plotScale);

    const colorScaleRange = normScaleRange.map(v =>
      Math.round(v * (gradientColors.length - 1))
    );
    this.colorScale = d3
      .scaleOrdinal()
      .range(colorScaleRange)
      .domain(this.domain)
      .unknown(undefined);

    this.colors = stringValues.map(this.colorScale);
    this.colorStartY = plotScale(0);
    this.colorEndY = plotScale(1);
  }
}

/* HELPER FUNCTIONS */

export function rgbaStr(rgba: RGBA) {
  const [r, g, b, a] = rgba;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function fakeScale(
  scale: (v: number | string | null) => number,
  invert: (v: number) => number | string,
  range: number[]
) {
  const s: any = scale.bind(null);
  s.invert = invert;
  s.range = () => range;
  s.copy = () => fakeScale(scale, invert, range);
  return s;
}
